'use client';

import { usePathname, useSearchParams } from 'next/navigation';

import { SpectatorPageFiltersForTeamsPage } from '@/components/organisms/SpectatorPageFiltersForTeamsPage';
import { SpectatorPagesFilter } from '@/components/organisms/SpectatorPagesFilter';
import { useDivisionsOptionsQuery } from '@/hooks/divisions/useDivisionsOptionsQuery';
import { useDistrictsInfoQuery } from '@/hooks/hooks';
import { useSeasonsOptionsQuery } from '@/hooks/seasons.ts/useSeasonsOptionsQuery';
import { useTeamLeagueDetailsQuery } from '@/hooks/team-leagues/useDetailsQuery';
import { useTeamsOptionsQuery } from '@/hooks/teams/useTeamsOptionsQuery';
import { Options } from '@/utils/types';

export const pageRoutes = {
	STANDINGS: '/standings',
	TEAMS: '/teams',
	PLAYERS: '/players',
	DIVISIONS: '/divisions',
	GAMEDAY: '/gameday',
	EVENT: '/event'
};

export const getLastPathSegment = (path: string) => {
	if (!path) return '';
	const segments = path.split('/').filter((segment) => segment !== '');
	if (segments.length === 0) return '/';
	return '/' + segments[segments.length - 1];
};

interface SpectatorPagesFilterSSRProps {
	teamLeagueUuid: string;
}

export default function SpectatorPagesFilterWrapper({ teamLeagueUuid }: SpectatorPagesFilterSSRProps) {
	const searchParams = useSearchParams();
	const pathname = usePathname();

	const currentRoute = getLastPathSegment(pathname);

	const { data: seasons, isLoading: isLoadingSeasons } = useSeasonsOptionsQuery(teamLeagueUuid);
	const { data: leagueInfoResponse, isLoading: isLoadingTLDetails } = useTeamLeagueDetailsQuery(teamLeagueUuid);

	const seasonIdForTeamDropdown =
		currentRoute === '/standings' || currentRoute === '/gameday'
			? (searchParams.get('season') ?? leagueInfoResponse?.data?.seasonUuid ?? '')
			: '';

	const { data: teams } = useTeamsOptionsQuery(teamLeagueUuid, seasonIdForTeamDropdown);
	const { data: divisions, isLoading: isLoadingDivisions } = useDivisionsOptionsQuery(teamLeagueUuid);
	const { data: districts, isLoading: isLoadingDistricts } = useDistrictsInfoQuery(teamLeagueUuid);

	if (isLoadingSeasons || isLoadingDivisions || isLoadingTLDetails || isLoadingDistricts) {
		return null;
	}

	const modifiedDistricts: Options[] = districts?.data?.teamLeagueDistricts
		? districts.data.teamLeagueDistricts.map((district) => ({
				value: district.uuid,
				label: district.title
			}))
		: [];

	if (currentRoute === pageRoutes.TEAMS) {
		return (
			<SpectatorPageFiltersForTeamsPage
				seasons={seasons?.data || []}
				divisions={divisions?.data || []}
				districts={modifiedDistricts}
				activeSeason={leagueInfoResponse?.data?.seasonUuid}
			/>
		);
	} else {
		return (
			<SpectatorPagesFilter
				seasons={seasons?.data || []}
				teams={teams?.data || []}
				divisions={divisions?.data || []}
				districts={modifiedDistricts}
				useScheduleGroups={leagueInfoResponse?.data?.useScheduleGroups || false}
				activeSeason={leagueInfoResponse?.data?.seasonUuid}
			/>
		);
	}
}
